import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from '@/components/shared/form-field';
import { PageHeader } from '@/components/shared/page-header';
import { ReadOnlyNotice } from '@/components/shared/read-only-notice';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toast';
import { useIsPlatformAdmin } from '@/hooks/use-is-platform-admin';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

interface GivingPaymentProvider {
  key: string;
  displayName: string;
  supportedMethods: string[];
  supportedCurrencies: string[];
}

interface PlatformGivingPaymentsConfig {
  enabledProviders: string[];
  availableProviders: GivingPaymentProvider[];
}

export const GivingPaymentsPage = () => {
  const { t } = useTranslation('admin');
  const isAdmin = useIsPlatformAdmin();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const [enabledProviders, setEnabledProviders] = useState<string[]>([]);

  const config = useQuery({
    queryKey: ['platform-giving-payments'],
    queryFn: () => api.get<PlatformGivingPaymentsConfig>('/platform/giving-payments'),
  });

  useEffect(() => {
    if (!config.data) return;
    setEnabledProviders(config.data.enabledProviders);
  }, [config.data]);

  const availableProviders = config.data?.availableProviders ?? [];

  const providerLabel = (key: string, displayName: string) => {
    const labelKey = `givingPayments.providers.${key}`;
    const translated = t(labelKey, { defaultValue: '' });
    return translated.length > 0 ? translated : displayName;
  };

  const providerSubtitle = (key: string) => {
    const subtitleKey = `givingPayments.providerSubtitle.${key}`;
    const translated = t(subtitleKey, { defaultValue: '' });
    return translated.length > 0 ? translated : null;
  };

  const methodLabels = useMemo(
    () =>
      availableProviders.map((provider) => ({
        key: provider.key,
        methods: provider.supportedMethods
          .map((method) => t(`givingPayments.methods.${method}`, { defaultValue: method }))
          .join(', '),
        currencies: provider.supportedCurrencies.join(', '),
      })),
    [availableProviders, t],
  );

  const toggleProvider = (key: string, checked: boolean) => {
    setEnabledProviders((current) => {
      if (checked) {
        return current.includes(key) ? current : [...current, key];
      }
      return current.filter((value) => value !== key);
    });
  };

  const save = useMutation({
    mutationFn: () => api.patch('/platform/giving-payments', { enabledProviders }),
    onSuccess: async () => {
      toast.success(t('givingPayments.saved'));
      await queryClient.invalidateQueries({ queryKey: ['platform-giving-payments'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const selectionHelp =
    enabledProviders.length === 0
      ? t('givingPayments.selectionHelp.none')
      : enabledProviders.length === availableProviders.length
        ? t('givingPayments.selectionHelp.all')
        : t('givingPayments.selectionHelp.some', { count: enabledProviders.length });

  return (
    <div>
      <PageHeader title={t('givingPayments.title')} description={t('givingPayments.description')} />
      <ReadOnlyNotice />

      <Card>
        <CardHeader>
          <CardTitle>{t('givingPayments.routingTitle')}</CardTitle>
          <CardDescription>{t('givingPayments.routingDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-3">
            {availableProviders.map((provider) => {
              const meta = methodLabels.find((entry) => entry.key === provider.key);
              const checked = enabledProviders.includes(provider.key);

              return (
                <div
                  key={provider.key}
                  className="flex items-start gap-3 rounded-md border border-border bg-surface-secondary px-3 py-3"
                >
                  <Checkbox
                    id={`giving-provider-${provider.key}`}
                    checked={checked}
                    onCheckedChange={(value) => toggleProvider(provider.key, value === true)}
                    aria-label={providerLabel(provider.key, provider.displayName)}
                  />
                  <div className="min-w-0 flex-1 space-y-1">
                    <Label
                      htmlFor={`giving-provider-${provider.key}`}
                      className="text-body-sm font-medium text-fg"
                    >
                      {providerLabel(provider.key, provider.displayName)}
                    </Label>
                    {providerSubtitle(provider.key) ? (
                      <p className="text-caption text-fg-secondary">
                        {providerSubtitle(provider.key)}
                      </p>
                    ) : null}
                    {meta ? (
                      <p className="text-caption text-fg-secondary">
                        {t('givingPayments.providerMeta', {
                          methods: meta.methods,
                          currencies: meta.currencies,
                        })}
                      </p>
                    ) : null}
                  </div>
                  <Badge variant={checked ? 'success' : 'neutral'}>
                    {checked ? t('givingPayments.enabled') : t('givingPayments.disabled')}
                  </Badge>
                </div>
              );
            })}
          </div>

          <FormField label={t('givingPayments.enabledProviders')} hint={selectionHelp}>
            {(field) => (
              <p {...field} className="text-body-sm text-fg-secondary">
                {enabledProviders.length === 0
                  ? t('givingPayments.noneSelected')
                  : enabledProviders
                      .map((key) => {
                        const provider = availableProviders.find((entry) => entry.key === key);
                        return provider
                          ? providerLabel(provider.key, provider.displayName)
                          : key;
                      })
                      .join(', ')}
              </p>
            )}
          </FormField>

          {isAdmin ? (
            <div className="flex justify-end">
              <Button
                loading={save.isPending}
                disabled={config.isLoading || enabledProviders.length === 0}
                onClick={() => save.mutate()}
              >
                {t('givingPayments.save')}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};
